import React, { useEffect, useState } from 'react';
import api, { API_BASE } from '@/lib/api';
import { useCycle } from '@/lib/cycle';
import { useAuth } from '@/lib/auth';
import { PageHeader, Card, Loading, Empty, SectionBadge } from '@/components/UI';
import { Download, Sparkles, RefreshCw, Check, AlertTriangle, CheckCircle2, CircleDashed } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime } from '@/lib/format';

export default function GeneratePPT() {
  const { currentCycle } = useCycle();
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [history, setHistory] = useState([]);
  const [templates, setTemplates] = useState(FALLBACK_TEMPLATES);
  const [template, setTemplate] = useState(() => {
    try { return normalizeTemplateId(localStorage.getItem('mrm_ppt_template')); } catch { return 'classic'; }
  });
  const selectTemplate = (id) => {
    setTemplate(id);
    try { localStorage.setItem('mrm_ppt_template', id); } catch { /* ignore */ }
  };
  useEffect(() => {
    api.get('/ppt/templates').then((r) => {
      const list = r.data?.templates || [];
      if (list.length) setTemplates(list);
    }).catch(() => {});
  }, []);
  const selectedTemplate = templates.find((t) => t.id === template) || templates[0];

  const loadPreview = async () => {
    if (!currentCycle) return;
    setLoading(true);
    try {
      const [pv, hist] = await Promise.all([
        api.post(`/ppt/preview/${currentCycle.id}`),
        api.get(`/ppt/list/${currentCycle.id}`).catch(() => ({ data: [] })),
      ]);
      // Development trace: the normalized dataset the deck will print (same object the backend logs).
      console.debug('[MRM] PPT DATA VALIDATION', pv.data?.cycle?.label, { validation: pv.data?.validation, scorecard: pv.data?.scorecard, rejection: pv.data?.rejection });
      setPreview(pv.data);
      setHistory(hist.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadPreview(); }, [currentCycle?.id]);

  const generate = async () => {
    const warns = preview?.data_coverage?.warnings || [];
    if (warns.length && !window.confirm(
      'This deck may be based on partial-month data:\n\n• ' + warns.join('\n• ') +
      '\n\nGenerate anyway?'
    )) return;
    setGenerating(true);
    try {
      const r = await api.post(`/ppt/generate/${currentCycle.id}`, { template });
      toast.success(`PPT generated — ${r.data.template_name || selectedTemplate?.name || 'template'} style`);
      const token = localStorage.getItem('mrm_token');
      const url = `${API_BASE}/ppt/download/${r.data.filename}`;
      // Trigger download with auth via fetch
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await resp.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = r.data.filename;
      link.click();
      window.URL.revokeObjectURL(link.href);
      loadPreview();
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); } finally { setGenerating(false); }
  };

  const downloadHistory = async (fname) => {
    const token = localStorage.getItem('mrm_token');
    const resp = await fetch(`${API_BASE}/ppt/download/${fname}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await resp.blob();
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fname;
    link.click();
  };

  if (!currentCycle) return <Empty title="No cycle selected" />;
  if (loading) return <Loading />;
  if (!preview) return <Empty title="Unable to load preview" />;

  const sections = preview.manual_sections || {};
  const canGenerate = user.role === 'admin' || user.role === 'management';

  return (
    <div className="space-y-6">
      <PageHeader title="Generate Presentation" subtitle={`Final PPT for ${currentCycle.label}`} actions={canGenerate && <button className="btn-gold text-sm" onClick={generate} disabled={generating} data-testid="btn-generate-ppt"><Sparkles size={14} className="inline mr-1" />{generating ? 'Generating…' : `Generate PPT · ${selectedTemplate?.name || ''}`}</button>} />
      <Card title="Select MRM Presentation Style" action={<span className="text-xs text-slate-500">Same data, sections &amp; calculations in every style — only the design changes</span>}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-4" data-testid="ppt-template-picker">
          {templates.map((t) => (
            <TemplateCard key={t.id} t={t} selected={t.id === template} onSelect={() => selectTemplate(t.id)} />
          ))}
        </div>
      </Card>
      <Card title="MRM Data Validation" action={<span className="text-xs text-slate-500">{currentCycle.label} · what the deck will print, in every template{preview.last_completed_period && !preview.last_completed_period.is_selected ? ` · note: last completed month is ${preview.last_completed_period.month}/${preview.last_completed_period.year}` : ''}</span>}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="text-sm font-semibold aampl-navy-text mb-3">Data Sources</div>
            <div className="space-y-1.5" data-testid="mrm-validation">
              {(preview.validation || []).map((v) => <ValidationRow key={v.key} v={v} />)}
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold aampl-navy-text mb-3">Executive Scorecard Preview</div>
            <div className="grid grid-cols-2 gap-2" data-testid="mrm-scorecard-preview">
              {(preview.scorecard || []).map((k) => <ScoreTile key={k.label} k={k} />)}
            </div>
          </div>
        </div>
      </Card>
      <Card title="Pre-Generation Summary">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-sm font-semibold aampl-navy-text mb-3">Auto-Pull (JSON)</div>
            <div className="space-y-2">
              <RowStat label="Production data" status={preview.has_production_data ? 'uploaded' : 'pending'} />
              <RowStat label="Maintenance data" status={preview.has_maintenance_data ? 'uploaded' : 'pending'} />
              <RowStat label="NPD data" status={preview.has_npd_data ? 'uploaded' : 'pending'} />
              <RowStat
                label="Schedule vs Supply (ERP)"
                status={preview.has_schedule_vs_supply ? 'uploaded' : 'pending'}
                note={preview.schedule_vs_supply_filename || null}
              />
            </div>
            {preview.data_coverage && (
              <div className="mt-3 text-xs" data-testid="coverage-summary">
                {['production', 'maintenance'].map((src) => {
                  const c = preview.data_coverage.coverage?.[src];
                  if (!c) return null;
                  return (
                    <div key={src} className="text-slate-600">
                      {src[0].toUpperCase() + src.slice(1)} coverage:{' '}
                      {c.max_date ? `${c.min_date} → ${c.max_date} (${c.days_with_data} day${c.days_with_data === 1 ? '' : 's'} of ${c.month_days})` : 'no dated records'}
                    </div>
                  );
                })}
                {(preview.data_coverage.warnings || []).map((w, i) => (
                  <div key={i} className="mt-1 text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid={`coverage-warning-${i}`}>⚠ {w}</div>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="text-sm font-semibold aampl-navy-text mb-3">Manual Sections</div>
            <div className="space-y-2">
              {['business_sales', 'quality', 'supply_chain', 'hr_safety', 'environment', 'change_points'].map((k) => (
                <RowStat
                  key={k}
                  label={k.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                  status={sections[k] || 'pending'}
                  note={k === 'business_sales' && preview.has_schedule_vs_supply ? 'auto-filled from Schedule vs Supply' : null}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
          <div><span className="text-slate-500">Total Actions:</span> <b>{preview.previous_actions_count}</b></div>
          <div><span className="text-slate-500">New Actions This Cycle:</span> <b>{preview.new_actions_count}</b></div>
        </div>
      </Card>
      <Card title="Previously Generated" action={<button className="btn-ghost text-sm" onClick={loadPreview}><RefreshCw size={14} className="inline" /></button>}>
        {history.length === 0 ? <div className="text-sm text-slate-500 py-4 text-center">No PPTs generated yet for this cycle.</div> : (
          <div className="aampl-table-wrap">
            <table className="aampl-table">
              <thead><tr><th>Filename</th><th>Style</th><th>Size</th><th>Modified</th><th></th></tr></thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.filename}>
                    <td className="font-mono text-xs">{h.filename}</td>
                    <td className="text-xs">{h.template_name || <span className="text-slate-400">Classic</span>}</td>
                    <td>{(h.size_bytes / 1024).toFixed(0)} KB</td>
                    <td>{formatDateTime(new Date(h.modified * 1000).toISOString())}</td>
                    <td><button className="btn-ghost text-sm" onClick={() => downloadHistory(h.filename)} data-testid={`download-ppt-${h.filename}`}><Download size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

const FALLBACK_TEMPLATES = [
  { id: 'classic', code: '01', name: 'Original / Classic', badge: 'ORIGINAL', renderer: 'classic', tagline: 'Navy · gold · off-white (original layout)', bg: '#F8F6F1', bg2: '#FFFFFF', glow: '#F8F6F1', primary: '#1E2761', primary_dark: '#131A45', accent: '#D4A843', accent_soft: '#F3E7C6', grad_a: '#1E2761', grad_mid: '#1E2761', grad_b: '#1E2761', heading: '#1E2761', muted: '#666666', card_border: '#E0E0E0' },
  { id: 'glossy_blue', code: '02', name: 'Glossy Blue', badge: 'BLUE', recommended: true, renderer: 'premium', tagline: 'Deep navy · royal blue · sky · subtle cyan', bg: '#F5F8FD', bg2: '#E4EDFB', glow: '#CBDDFB', primary: '#1D4ED8', primary_dark: '#0B1F4D', accent: '#2F80FF', accent_soft: '#E3EDFF', highlight: '#7DD3FC', grad_a: '#0B1F4D', grad_mid: '#1D4ED8', grad_b: '#5AB0F6', heading: '#0F1B3D', muted: '#5B6B8C', card_border: '#D5E0F5' },
  { id: 'purple', code: '03', name: 'Premium Purple', badge: 'PURPLE', renderer: 'premium', tagline: 'Deep indigo · royal purple · violet · lavender', bg: '#F7F6FC', bg2: '#EBE7F9', glow: '#DCD3F7', primary: '#5B3FBF', primary_dark: '#2A1F6B', accent: '#7C5CE6', accent_soft: '#ECE6FB', highlight: '#9ADBF3', grad_a: '#2A1F6B', grad_mid: '#5B3FBF', grad_b: '#9B84EE', heading: '#1E1A3C', muted: '#6B6490', card_border: '#DDD6F3' },
  { id: 'green', code: '04', name: 'Emerald Green', badge: 'GREEN', renderer: 'premium', tagline: 'Deep emerald · forest · teal green · mint', bg: '#F4F9F7', bg2: '#E1F0EA', glow: '#CFEBDD', primary: '#0F7A5A', primary_dark: '#0B3D2E', accent: '#19A383', accent_soft: '#DDF3EC', highlight: '#8EE0D0', grad_a: '#0B3D2E', grad_mid: '#0F8A6A', grad_b: '#8FD9BC', heading: '#0D2B22', muted: '#587A6E', card_border: '#CFE4DB' },
  { id: 'teal', code: '05', name: 'Industrial Teal', badge: 'TEAL', renderer: 'premium', tagline: 'Deep teal · dark cyan · turquoise · aqua', bg: '#F3F9FB', bg2: '#DDF0F5', glow: '#C9E9F2', primary: '#0E7490', primary_dark: '#083B4A', accent: '#14B0C8', accent_soft: '#DBF3F8', highlight: '#A5EEF2', grad_a: '#083B4A', grad_mid: '#0E8CA6', grad_b: '#8FE3EC', heading: '#0B2A33', muted: '#4F7480', card_border: '#CFE6EC' },
];
// Retired ids (Nude → Purple, Warm → Teal) and the first picker's ids still resolve.
const LEGACY_IDS = { corporate: 'glossy_blue', premium: 'purple', nude: 'purple', warm: 'teal' };
function normalizeTemplateId(id) {
  const v = LEGACY_IDS[id] || id;
  return FALLBACK_TEMPLATES.some((t) => t.id === v) ? v : 'classic';
}

const STATE_META = {
  loaded: { icon: CheckCircle2, cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', text: 'Loaded' },
  zero: { icon: CheckCircle2, cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', text: 'Value = 0' },
  pending: { icon: CircleDashed, cls: 'text-amber-800 bg-amber-50 border-amber-200', text: 'Data pending' },
  unavailable: { icon: AlertTriangle, cls: 'text-rose-800 bg-rose-50 border-rose-200', text: 'Data unavailable' },
};

function ValidationRow({ v }) {
  const m = STATE_META[v.state] || STATE_META.pending;
  const Icon = m.icon;
  return (
    <div className={`flex items-center justify-between gap-3 text-sm border rounded px-2.5 py-1.5 ${m.cls}`} data-testid={`validation-${v.key}`}>
      <span className="flex items-center gap-2 min-w-0">
        <Icon size={14} className="shrink-0" />
        <span className="font-medium truncate">{v.label}</span>
      </span>
      <span className="text-[11px] text-right shrink-0 max-w-[60%]">
        <b>{m.text}</b>
        {v.records !== null && v.records !== undefined ? <span className="ml-1 px-1.5 py-0.5 rounded bg-white/70 border border-current/20 font-semibold" data-testid={`validation-records-${v.key}`}>{v.records} {v.records === 1 ? 'record' : 'records'}</span> : null}
        {v.detail ? <span className="opacity-80 block truncate" title={v.detail}> · {v.detail}</span> : null}
      </span>
    </div>
  );
}

function ScoreTile({ k }) {
  const na = k.status === 'na';
  const dot = { green: '#2E7D32', amber: '#E65100', red: '#C62828', na: '#8A8F98' }[k.status] || '#8A8F98';
  return (
    <div className="border border-[var(--aampl-border)] rounded px-3 py-2 bg-white" data-testid={`score-${k.label}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide text-slate-500 truncate">{k.label}</span>
        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: dot }} />
      </div>
      <div className={`font-bold ${na ? 'text-slate-400 text-sm' : 'text-lg aampl-navy-text'}`}>{k.value}</div>
      <div className="text-[10px] text-slate-500 truncate">{k.target && k.target !== '-' ? `Target ${k.target}` : ''}{k.note ? `${k.target && k.target !== '-' ? ' · ' : ''}${k.note}` : ''}</div>
    </div>
  );
}

/* Miniature, CSS-only mock of a data slide (gradient header band + KPI cards) so the user can compare styles at a glance. */
function TemplateCard({ t, selected, onSelect }) {
  const band = `linear-gradient(90deg, ${t.grad_a}, ${t.grad_mid || t.primary})`;
  const bar = `linear-gradient(90deg, ${t.grad_mid || t.primary}, ${t.grad_b})`;
  const kpi = (i) => (
    <div key={i} style={{ background: '#fff', border: `1px solid ${t.card_border}`, borderRadius: 5, padding: '5px 6px', boxShadow: `0 2px 6px ${t.primary_dark}14`, position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 5, right: 5, height: 2, background: bar }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
        <span style={{ width: 7, height: 7, borderRadius: 2, background: t.accent_soft, display: 'inline-block' }} />
        <span style={{ height: 3, width: '55%', borderRadius: 2, background: t.muted, opacity: 0.4, display: 'inline-block' }} />
        <span style={{ marginLeft: 'auto', width: 4, height: 4, borderRadius: 2, background: ['#1E8E5A', '#C9A227', '#1E8E5A', '#8A94A8'][i], display: 'inline-block' }} />
      </div>
      <div style={{ marginTop: 5, height: 7, width: '48%', borderRadius: 2, background: t.heading, opacity: 0.9 }} />
    </div>
  );
  const badgeStyle = { fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', padding: '2px 7px', borderRadius: 4 };
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={`ppt-template-${t.id}`}
      aria-pressed={selected}
      className="text-left rounded-xl transition-all focus:outline-none hover:-translate-y-0.5"
      style={{
        border: `2px solid ${selected ? t.primary : 'var(--aampl-border)'}`,
        boxShadow: selected ? `0 0 0 4px ${t.accent_soft}, 0 10px 28px ${t.primary_dark}1f` : '0 1px 3px rgba(0,0,0,0.05)',
        background: '#fff',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {t.recommended && (
        <span data-testid="ppt-template-recommended" style={{ ...badgeStyle, position: 'absolute', top: 10, right: 10, zIndex: 2, color: '#fff', background: t.primary, boxShadow: '0 2px 8px rgba(0,0,0,0.18)' }}>★ RECOMMENDED</span>
      )}
      {/* preview */}
      {t.renderer === 'classic' ? <ClassicPreview t={t} /> : (
      <div style={{ background: `radial-gradient(circle at 92% 40%, ${t.glow} 0, transparent 45%), radial-gradient(circle at -5% 110%, ${t.bg2} 0, transparent 40%), ${t.bg}`, aspectRatio: '16 / 9', position: 'relative', borderBottom: `1px solid ${t.card_border}`, overflow: 'hidden' }}>
        {/* glossy gradient header band */}
        <div style={{ background: band, height: '27%', position: 'relative', padding: '7px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ position: 'absolute', right: '-10%', top: '-120%', width: '70%', height: '220%', borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <div>
            <div style={{ color: '#fff', fontSize: 9.5, fontWeight: 800, letterSpacing: '-0.01em' }}>Executive Summary — Scorecard</div>
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 5.5, marginTop: 1 }}>SANBER LABS — SEPTEMBER 2026</div>
          </div>
          {!t.recommended && <span style={{ fontSize: 5.5, fontWeight: 700, color: '#fff', background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.5)', padding: '2px 6px', borderRadius: 999 }}>IATF / ISO · 9.3.2</span>}
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 2, background: `linear-gradient(90deg, ${t.grad_mid || t.primary}, ${t.highlight || t.grad_b}, ${t.grad_b})` }} />
        </div>
        <div style={{ padding: 10 }}>
          <div style={{ background: 'rgba(255,255,255,0.6)', border: '1px solid #fff', borderRadius: 6, padding: 6, boxShadow: `0 4px 14px ${t.primary_dark}12` }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 5 }}>{[0, 1, 2, 3].map(kpi)}</div>
          </div>
          <div style={{ marginTop: 7, border: `1px solid ${t.card_border}`, borderRadius: 4, overflow: 'hidden', background: '#fff' }}>
            <div style={{ background: t.primary, height: 7 }} />
            {[0, 1].map((i) => <div key={i} style={{ height: 6, background: i % 2 ? t.bg : '#fff' }} />)}
          </div>
        </div>
        <div style={{ position: 'absolute', left: 10, right: 10, bottom: 5, display: 'flex', justifyContent: 'space-between', fontSize: 4.5, color: t.muted }}>
          <span>AAMPL | Management Review</span><span style={{ color: t.primary, fontWeight: 700 }}>CONFIDENTIAL</span>
        </div>
      </div>
      )}
      {/* meta */}
      <div className="px-4 py-3 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span style={{ ...badgeStyle, color: t.primary, background: t.accent_soft }}>{t.badge || `TEMPLATE ${t.code}`}</span>
            <span className="text-[11px] uppercase tracking-wider text-slate-500">Template {t.code}</span>
          </div>
          <div className="font-semibold mt-1" style={{ color: 'var(--aampl-text)' }}>{t.name}</div>
          <div className="text-xs text-slate-500 mt-0.5">{t.tagline}</div>
          <div className="flex gap-1 mt-2">
            {[t.primary_dark, t.primary, t.accent, t.accent_soft, t.bg2].map((c, i) => (
              <span key={i} style={{ width: 14, height: 14, borderRadius: 4, background: c, border: '1px solid rgba(0,0,0,0.06)', display: 'inline-block' }} />
            ))}
          </div>
        </div>
        <span
          className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md"
          style={selected ? { background: t.primary, color: '#fff' } : { background: 'transparent', color: 'var(--aampl-navy)', border: '1px solid var(--aampl-border)' }}
        >
          {selected ? <><Check size={12} /> SELECTED</> : 'SELECT'}
        </span>
      </div>
    </button>
  );
}

/* Mini mock of the original navy/gold deck (title bar, gold rule, left-accent KPI cards). */
function ClassicPreview({ t }) {
  return (
    <div style={{ background: t.bg, aspectRatio: '16 / 9', position: 'relative', borderBottom: `1px solid ${t.card_border}`, overflow: 'hidden' }}>
      <div style={{ background: t.primary, height: '24%', padding: '8px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ color: '#fff', fontSize: 9, fontWeight: 700, fontFamily: 'Georgia, serif' }}>Management Review Meeting</span>
        <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.08em', color: t.primary, background: t.accent, padding: '2px 6px', borderRadius: 3 }}>TEMPLATE {t.code}</span>
      </div>
      <div style={{ height: 3, background: t.accent }} />
      <div style={{ padding: 10 }}>
        <div style={{ fontSize: 7, fontWeight: 700, color: t.primary, fontFamily: 'Georgia, serif' }}>SANBER LABS — SEPTEMBER 2026</div>
        <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
          {['#2E7D32', '#E65100', '#2E7D32', '#C62828'].map((c, i) => (
            <div key={i} style={{ background: '#fff', border: '1px solid #E0E0E0', borderRadius: 3, padding: '4px 5px 4px 8px', position: 'relative' }}>
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: c }} />
              <div style={{ height: 6, width: '45%', background: '#1A1A1A', opacity: 0.85, borderRadius: 2 }} />
              <div style={{ marginTop: 3, height: 3, width: '65%', background: '#555', opacity: 0.4, borderRadius: 2 }} />
            </div>
          ))}
        </div>
        <div style={{ marginTop: 6, border: '1px solid #E0E0E0', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ background: t.primary, height: 7 }} />
          {[0, 1, 2].map((i) => <div key={i} style={{ height: 6, background: i % 2 ? '#F5F5F5' : '#fff' }} />)}
        </div>
      </div>
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 7, background: t.primary }} />
    </div>
  );
}

function RowStat({ label, status, note }) {
  return (
    <div className="flex justify-between items-center text-sm border-b border-[var(--aampl-border)] pb-1">
      <span>
        {label}
        {note && <span className="ml-2 text-[11px] text-emerald-600">• {note}</span>}
      </span>
      <SectionBadge status={status} label={status.toUpperCase()} />
    </div>
  );
}
